import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Action,
  Effect,
  FunctionHeaderInterface,
  FunctionInterface,
  FunctionKeyInterface,
  FunctionParameterInterface,
  FunctionParameterParameterLocation,
  NamespaceInterface,
} from '@vendasta/ai-assistants';
import { AiToolAuthStrategyForm } from '../../components/tools/ai-tool-auth-strategy-form/ai-tool-auth-strategy-form.component';
import { AiToolFormRegistryService } from '../services/ai-tool-form-registry.service';
import { AiConstraintsForm } from './ai-constraints.form';
import { FormArrayWithDirtyTracking } from './forms';
import { requireUrlAndMethodWhenNotGeneratingAnswer } from './forms.validators';

// Tools
type AiToolFormMetadata = {
  namespace?: NamespaceInterface;
} & (
  | {
      isNew: false;
      localId?: undefined;
    }
  | {
      isNew: true;
      localId: string;
    }
);

export type ToFunctionOptions = {
  excludeAuthHeaders?: boolean;
};

export type AiToolFormControls = {
  id: FormControl<string | null>;
  namespace: FormControl<NamespaceInterface | null>;
  generatesAnswer: FormControl<boolean | null>;
  description: FormControl<string | null>;
  method: FormControl<string | null>;
  url: FormControl<string | null>;
  headers: AiToolHeaderFormArray;
  parameters: AiToolParameterFormArray;
  authStrategy: AiToolAuthStrategyForm;
  constraints: AiConstraintsForm;
  mcpId: FormControl<string | null>;
  noOp: FormControl<boolean | null>;
};

type AiToolFormOptions = {
  func?: FunctionInterface;
  metadata?: AiToolFormMetadata;
  defaultHidden?: boolean;
};

export class AiToolForm extends FormGroup<AiToolFormControls> {
  metadata: AiToolFormMetadata;
  sourcePath: string;
  private _originalValues: unknown; // Type could be defined as ~ ReturnValue<FormGroup<AiToolFormControls>, "getRawValue">

  constructor(opts?: AiToolFormOptions) {
    const {
      func,
      metadata = {
        isNew: false,
        namespace: func?.namespace,
      },
      defaultHidden = false,
    } = opts ?? {};

    super(
      {
        id: new FormControl(
          {
            value: func?.id ?? null,
            disabled: !metadata.isNew,
          },
          [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]{1,64}$/)],
        ),
        namespace: new FormControl({ value: func?.namespace ?? null, disabled: true }),
        generatesAnswer: new FormControl({ value: func?.generatesAnswer ?? false, disabled: true }),
        description: new FormControl(func?.description ?? null, Validators.required),
        method: new FormControl(func?.generatesAnswer ? null : func?.methodType || 'GET'),
        url: new FormControl(func?.url ?? null),
        headers: new AiToolHeaderFormArray(func?.headers ?? []),
        parameters: new AiToolParameterFormArray(func?.functionParameters ?? []),
        authStrategy: new AiToolAuthStrategyForm(func?.authStrategy),
        constraints: new AiConstraintsForm(
          func?.constraints ||
            (!func?.id && defaultHidden
              ? [
                  {
                    action: Action.ACTION_VIEW,
                    effect: Effect.EFFECT_DENY,
                    scope: undefined,
                  },
                ]
              : []),
        ),
        mcpId: new FormControl(func?.mcpId ?? null),
        noOp: new FormControl(func?.noOp ?? null),
      },
      { validators: [requireUrlAndMethodWhenNotGeneratingAnswer()] },
    );
    this.metadata = metadata;
    this._originalValues = this.getRawValue();

    this.controls.namespace.disable();
    if (func?.managed || func?.namespace?.globalNamespace) {
      this.disable();
    }
    this.sourcePath = func?.sourcePath || '';
  }

  override get dirty(): boolean {
    // Check if any values have changed from the original
    if (this._originalValues) {
      return JSON.stringify(this.getRawValue()) !== JSON.stringify(this._originalValues);
    }
    return super.dirty;
  }

  override markAsPristine(): void {
    super.markAsPristine();
    // Update the original values to match current values when marked as pristine
    this._originalValues = this.getRawValue();
  }

  merge(other: AiToolForm) {
    this.controls.headers.clear();
    other.controls.headers.controls.forEach((otherHeaderForm) => this.controls.headers.push(otherHeaderForm));

    this.controls.parameters.clear();
    other.controls.parameters.controls.forEach((otherParamForm) => this.controls.parameters.push(otherParamForm));

    // Manually handle the constraints assignmentConstraints FormArray
    this.controls.constraints.controls.assignmentConstraints.clear();
    other.controls.constraints.controls.assignmentConstraints.controls.forEach((constraintForm) => {
      this.controls.constraints.controls.assignmentConstraints.push(constraintForm);
    });

    this.metadata = { ...other.metadata };

    // Copy the values
    this.setValue(other.getRawValue());

    // Recursively transfer dirty state from other form
    this.transferDirtyStateFrom(other);
  }

  private transferDirtyStateFrom(other: AbstractControl) {
    if (other instanceof FormGroup) {
      Object.keys(other.controls).forEach((key) => {
        const thisControl = this.get(key);
        const otherControl = other.get(key);
        if (thisControl && otherControl) {
          if (otherControl.dirty) {
            thisControl.markAsDirty({ emitEvent: true });
          }
          if (otherControl instanceof FormGroup || otherControl instanceof FormArray) {
            this.transferDirtyStateFrom(otherControl);
          }
        }
      });
    }
  }

  toFunction(fallbackNamespace?: NamespaceInterface | undefined, options?: ToFunctionOptions): FunctionInterface {
    const { id, description, method, url, generatesAnswer, namespace, mcpId, noOp } = this.getRawValue();
    const headers = options?.excludeAuthHeaders
      ? this.controls.headers.toFunctionHeaders().filter((header) => header.key !== 'Authorization')
      : this.controls.headers.toFunctionHeaders();
    const parameters = this.controls.parameters.toFunctionParameters();
    const constraints = this.controls.constraints.toConstraints();

    if (noOp) {
      return {
        id: id ?? undefined,
        namespace: namespace ?? fallbackNamespace ?? undefined,
        description: description ?? undefined,
        functionParameters: parameters.length > 0 ? parameters : undefined,
        constraints: constraints.length > 0 ? constraints : undefined,
        noOp: noOp ?? undefined,
      };
    }

    return {
      id: id ?? undefined,
      namespace: namespace ?? fallbackNamespace ?? undefined,
      description: description ?? undefined,
      methodType: method ?? undefined,
      url: url ?? undefined,
      generatesAnswer: generatesAnswer ?? undefined,
      headers: headers.length > 0 ? headers : undefined,
      functionParameters: parameters.length > 0 ? parameters : undefined,
      constraints: constraints.length > 0 ? constraints : undefined,
      authStrategy: this.controls.authStrategy.authStrategy,
      mcpId: mcpId ?? undefined,
      noOp: noOp ?? undefined,
    };
  }

  toFunctionKey(fallbackNamespace: NamespaceInterface | undefined): FunctionKeyInterface {
    return {
      id: this.controls.id.getRawValue() ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? fallbackNamespace ?? undefined,
    };
  }

  getFirstErrorMessageI18N(): string {
    const controls = this.controls;

    // Check FormGroup-level errors first (cross-field validation)
    if (this.errors) {
      if (this.errors['methodRequired']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.METHOD_REQUIRED';
      }
      if (this.errors['urlRequired']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.URL_REQUIRED';
      }
      if (this.errors['urlInvalid']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.URL_INVALID';
      }
    }

    if (controls.id.errors) {
      if (controls.id.errors['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.ID_REQUIRED';
      }
      if (controls.id.errors['pattern']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.ID_PATTERN';
      }
    }

    if (controls.description.errors) {
      if (controls.description.errors['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.DESCRIPTION_REQUIRED';
      }
    }

    // Check headers errors
    const invalidHeaders = controls.headers.controls.filter((header) => header.invalid);
    if (invalidHeaders.length > 0) {
      const firstInvalidHeader = invalidHeaders[0];
      if (firstInvalidHeader.controls.name.errors?.['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.HEADER_NAME_REQUIRED';
      }
      if (firstInvalidHeader.controls.value.errors?.['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.HEADER_VALUE_REQUIRED';
      }
    }

    // Check parameters errors
    const invalidParameters = controls.parameters.controls.filter((param) => param.invalid);
    if (invalidParameters.length > 0) {
      const firstInvalidParam = invalidParameters[0];
      if (firstInvalidParam.controls.name.errors?.['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.PARAMETER_NAME_REQUIRED';
      }
      if (firstInvalidParam.controls.type.errors?.['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.PARAMETER_TYPE_REQUIRED';
      }
      if (firstInvalidParam.controls.location.errors?.['required']) {
        return 'AI_ASSISTANT.FUNCTIONS.ERRORS.PARAMETER_LOCATION_REQUIRED';
      }
    }

    // Fallback to generic message if no specific error found
    return 'AI_ASSISTANT.GOALS.CONFIGURE_GOAL.FORM_INVALID';
  }
}

export class AiToolFormArray extends FormArrayWithDirtyTracking<AiToolForm> {
  private registry?: AiToolFormRegistryService;

  constructor(functions: FunctionInterface[], registry?: AiToolFormRegistryService) {
    if (registry) {
      const forms = functions.map((func) => {
        const toolId = func.id;
        if (toolId) {
          return registry.getOrRegister(toolId, () => new AiToolForm({ func }));
        }
        return new AiToolForm({ func });
      });
      super(forms);

      this.registry = registry;

      // Register this array as a parent for each tool
      forms.forEach((form) => {
        const toolId = form.controls.id.value;
        if (toolId) {
          registry.registerParent(toolId, this);
        }
      });
    } else {
      super(functions.map((func) => new AiToolForm({ func })));
    }
  }

  override push(form: AiToolForm, options?: { emitEvent?: boolean }): void {
    super.push(form, options);

    // Register this array as parent of the new tool
    if (this.registry) {
      const toolId = form.controls.id.value;
      if (toolId) {
        this.registry.registerParent(toolId, this);
      }
    }
  }

  override removeAt(index: number, options?: { emitEvent?: boolean }): void {
    // Unregister before removing
    if (this.registry) {
      const form = this.at(index) as AiToolForm;
      const toolId = form.controls.id.value;
      if (toolId) {
        this.registry.unregisterParent(toolId, this);
      }
    }

    super.removeAt(index, options);
  }

  override clear(options?: { emitEvent?: boolean }): void {
    // Unregister all tools when clearing
    if (this.registry) {
      this.controls.forEach((form) => {
        const toolId = form.controls.id.value;
        if (toolId) {
          this.registry!.unregisterParent(toolId, this);
        }
      });
    }

    super.clear(options);
  }

  /**
   * Clean up parent registrations
   * Call this when the capability/form containing this array is destroyed
   */
  unregisterAll(): void {
    if (this.registry) {
      this.controls.forEach((form) => {
        const toolId = form.controls.id.value;
        if (toolId) {
          this.registry!.unregisterParent(toolId, this);
        }
      });
    }
  }

  toFunctions(fallbackNamespace: NamespaceInterface | undefined, options?: ToFunctionOptions): FunctionInterface[] {
    return this.controls.map((toolForm) => toolForm.toFunction(fallbackNamespace, options));
  }

  toFunctionKeys(fallbackNamespace: NamespaceInterface | undefined): FunctionKeyInterface[] {
    return this.controls.map((toolForm) => toolForm.toFunctionKey(fallbackNamespace));
  }
}

// Tool Headers
export type AiToolHeaderFormControls = {
  name: FormControl<string | null>;
  value: FormControl<string | null>;
};

export class AiToolHeaderForm extends FormGroup<AiToolHeaderFormControls> {
  constructor(header?: FunctionHeaderInterface) {
    super({
      name: new FormControl(header?.key ?? null, Validators.required),
      value: new FormControl(header?.value ?? null, Validators.required),
    });
  }

  toFunctionHeader(): FunctionHeaderInterface {
    const { name, value } = this.getRawValue();
    return {
      key: name ?? undefined,
      value: value ?? undefined,
    };
  }
}

export class AiToolHeaderFormArray extends FormArrayWithDirtyTracking<AiToolHeaderForm> {
  constructor(headers: FunctionHeaderInterface[]) {
    super(headers.map((header) => new AiToolHeaderForm(header)));
  }

  toFunctionHeaders() {
    return this.controls.map((headerForm) => headerForm.toFunctionHeader());
  }
}

// Tool Parameters
export type AiToolParameterFormControls = {
  name: FormControl<string | null>;
  description: FormControl<string | null>;
  type: FormControl<string | null>;
  location: FormControl<FunctionParameterParameterLocation | null>;
  automaticallyFilled: FormControl<boolean | null>;
  items?: AiToolParameterForm;
  nestedParameters: AiToolParameterFormArray;
  required?: FormControl<boolean | null>;
  value?: FormControl<string | null>;
  enumValues?: FormArray<FormControl<string | null>>;
};

export class AiToolParameterForm extends FormGroup<AiToolParameterFormControls> {
  constructor(toolParameter?: FunctionParameterInterface, isArrayItem?: boolean) {
    super({
      name: new FormControl(toolParameter?.name ?? null, isArrayItem ? null : Validators.required),
      description: new FormControl(toolParameter?.description ?? null),
      type: new FormControl(toolParameter?.type ?? 'string', Validators.required),
      location: new FormControl(
        toolParameter?.location ?? FunctionParameterParameterLocation.LOCATION_BODY,
        Validators.required,
      ),
      automaticallyFilled: new FormControl(false),
      nestedParameters: new AiToolParameterFormArray(toolParameter?.properties ?? []),
      required: new FormControl(toolParameter?.required ?? false),
      value: new FormControl(
        toolParameter?.type === 'array' || toolParameter?.type === 'object' ? null : (toolParameter?.value ?? null),
      ),
      enumValues: new FormArray(
        (toolParameter?.enumValues || []).map((value) => new FormControl(value, Validators.required)),
      ),
    });
    if (toolParameter?.type?.toLowerCase() === 'array' && toolParameter?.items) {
      this.setControl('items', new AiToolParameterForm(toolParameter.items, true));
    }

    // Clear value when type changes to array or object
    // No need to unsubscribe - the subscription will be garbage collected with the form
    this.controls.type.valueChanges.subscribe((type) => {
      if (type === 'array' || type === 'object') {
        this.controls.value?.setValue(null);
      }
    });
  }

  clone(): AiToolParameterForm {
    const clone = new AiToolParameterForm();

    clone.controls.nestedParameters.controls.push(
      ...(this.controls.nestedParameters.controls.map((nestedForm) => nestedForm.clone()) ?? []),
    );

    clone.patchValue(this.getRawValue());
    return clone;
  }

  merge(other: AiToolParameterForm) {
    this.controls.nestedParameters.clear();
    other.controls.nestedParameters.controls.forEach((otherParamForm) =>
      this.controls.nestedParameters.push(otherParamForm),
    );
    this.patchValue(other.getRawValue());
  }

  toFunctionParameter(): FunctionParameterInterface {
    const { name, description, type, location, required, value } = this.getRawValue();
    const nestedParameters = this.controls.nestedParameters.controls.map((nestedForm) =>
      nestedForm.toFunctionParameter(),
    );
    const enumValues = this.controls.enumValues?.controls?.map((ctrl) => ctrl.getRawValue() ?? '').filter((val) => val);

    return {
      name: name ?? undefined,
      description: description ?? undefined,
      type: type ?? undefined,
      location: location ?? undefined,
      properties: type === 'object' ? nestedParameters : undefined,
      items: type === 'array' ? this.controls.items?.toFunctionParameter() : undefined,
      required: required ?? false,
      value: value ?? undefined,
      enumValues: enumValues ?? undefined,
    };
  }
}

export class AiToolParameterFormArray extends FormArrayWithDirtyTracking<AiToolParameterForm> {
  constructor(parameters: FunctionParameterInterface[]) {
    super(parameters.map((param) => new AiToolParameterForm(param)));
  }

  toFunctionParameters() {
    return this.controls.map((paramForm) => paramForm.toFunctionParameter());
  }
}
